import { useEffect } from 'react';

import { ApiSectionNamespace } from '@api';
import { useApplicationRuntimeContext } from '@context/ApplicationRuntimeContext';
import { LoggerType } from '@services/errorService';
import {
  useHttpClientAsync,
  GRAPHQL_HEADER,
  httpMethods,
  IGeneralRequestResponse,
  IGraphQlQuery,
  IHttpClientResponse,
} from '@services/httpClientService';
import { nonAwaitedPromiseHandlerParametrized } from '@utils/async';

import { ICartPageSettings } from '../../../model';
import { transformCartPageSettings } from '../../mapper';

import { CART_PAGE_CONFIGURATION_QUERY } from './cartPageConfigurationQuery';
import { cartPageConfigurationFromDto } from './configurationResponse.decoder';
import { ICartPageSettingsGatewayResponse } from './model';

const mappingFunction = (
  result: ICartPageSettingsGatewayResponse,
  logger: LoggerType,
  currentCountry: string,
): IGeneralRequestResponse<ICartPageSettings> | null => {
  const messages: string[] = [
    ...result.aggregatedConfiguration.deliveryConfiguration.messages,
    ...result.aggregatedConfiguration.cartConfiguration.messages,
  ];

  const decoded = cartPageConfigurationFromDto(result, logger);

  return {
    data: transformCartPageSettings(decoded, currentCountry),
    messages: messages,
  };
};

export const useCartPageConfigurationOnRender = (): IHttpClientResponse<
  IGeneralRequestResponse<ICartPageSettings>,
  IGraphQlQuery
> => {
  const {
    applicationSettings: {
      localization: { countryIdentifier },
    },
  } = useApplicationRuntimeContext();

  const asyncRequest = useHttpClientAsync<
    IGeneralRequestResponse<ICartPageSettings>,
    IGraphQlQuery,
    ICartPageSettingsGatewayResponse
  >({
    method: httpMethods.POST,
    path: ApiSectionNamespace.ECartPageApiKeys.CART_PAGE_AGGREGATED_CONFIGURATION,
    mappingFunction: (result, logger) => mappingFunction(result, logger, countryIdentifier),
    customHeaders: GRAPHQL_HEADER,
  });

  useEffect(() => {
    if (countryIdentifier) {
      nonAwaitedPromiseHandlerParametrized(asyncRequest.request, { data: { query: CART_PAGE_CONFIGURATION_QUERY } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryIdentifier]);

  return asyncRequest;
};
